import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, 
         AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Check, AlertCircle } from 'lucide-react';
import { ProjectService } from '@/services/ProjectService';
import ProjectHeader from '@/components/header';
import ScrollFeedbackSurvey from '@/components/ScrollFeedbackSurvey';
import { StyleSection } from '@/components/summaryComponents/StyleSection';
import { AppliancesSection } from '@/components/summaryComponents/AppliancesSection';
import { LayoutSection } from '@/components/summaryComponents/LayoutSection';
import { FilesSection } from '@/components/summaryComponents/FilesSection';
import { AdditionalInfoSection } from '@/components/summaryComponents/AdditionalInfoSection';
import { Button } from '@/components/ui/button';

const ProjectSummary = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showFeedback, setShowFeedback] = useState(true);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const project = await ProjectService.getProjectById(projectId);
        setProjectData(project);
      } catch (error) {
        console.error('Error fetching project data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  const handleFeedbackComplete = async (feedbackData) => {
    await ProjectService.updateProject(projectId, {"survay": feedbackData});
    setShowFeedback(false);
  };

  const handleFeedbackDismiss = () => {
    setShowFeedback(false);
  };

  const handleSubmitProject = async () => {
    try {
      // await ProjectService.submitProject(projectId);
      await ProjectService.submitProjects([projectId]);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error submitting project:', error);
    }
  };

  const handleDone = () => {
    setShowConfirmation(false);
    navigate('/projects');
  };

  const handleEditSection = (path) => {
    navigate(`/project/${projectId}/${path}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  const isEditable = projectData?.status === 'in_progress';

  return (
    <div className="min-h-screen bg-gray-100 pb-24">
      <ProjectHeader showBackButton={true} />
      <main className="pt-20 px-4 max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Project Summary</h1>
          <p className="text-gray-600">
            {projectData?.status === 'submitted' 
              ? 'Your project has been submitted. Here\'s a summary of your requirements.'
              : 'Review your project details before submission. You can still make changes if needed.'}
          </p>
        </div>

        {!projectData?.status && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please review all details carefully before submitting. You can edit any section by clicking the Edit button.
            </AlertDescription>
          </Alert>
        )}

        <StyleSection projectData={projectData} onEdit={handleEditSection} isEditable={isEditable} />
        <AppliancesSection projectData={projectData} onEdit={handleEditSection} isEditable={isEditable} />
        <LayoutSection projectData={projectData} onEdit={handleEditSection} isEditable={isEditable} />
        <FilesSection projectData={projectData} onEdit={handleEditSection} isEditable={isEditable} />
        <AdditionalInfoSection projectData={projectData} onEdit={handleEditSection} isEditable={isEditable} />

        {isEditable && (
          <div className="mt-8 flex justify-end">
            <Button
              size="lg"
              onClick={handleSubmitProject}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Check className="mr-2 h-5 w-5" /> Request design and quote
            </Button>
          </div>
        )}

        <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-2xl font-bold text-center mb-2">
                Congratulations! 🎉
              </AlertDialogTitle>
              <AlertDialogDescription className="text-center space-y-4">
                <p>Thank you for submitting your kitchen project!</p>
                <p>Our team will carefully review your files and requirements to provide you with a custom design and detailed quote.</p>
                <p className="font-medium">Please check your email for updates on your project.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex justify-center">
              <AlertDialogAction 
                onClick={handleDone}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Done
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {isEditable && showFeedback && (
          <ScrollFeedbackSurvey
            onComplete={handleFeedbackComplete}
            onDismiss={handleFeedbackDismiss}
          />
        )}
      </main>
    </div>
  );
};

export default ProjectSummary;